import { useMemo, useState, useEffect } from "react";
import { Form, Input, Select, Button, InputNumber, Checkbox, Row, Col, Space, TimePicker, notification } from "antd";
import { HomeOutlined, PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import "./styles.scss";
import { useGetBranchesQuery } from "../../services/branches";
import PageBreadcrumb from "../../components/breadcrumb";
import { AllPackagesRoute, Home } from "../../routes/routepath";
import { useNavigate } from "react-router-dom";
import ImagePicker from "../../components/form/ImagePicker";
import { useAddPlanMutation } from "../../services/package";
import { useGetGymKitInventoryQuery } from "../../services/inventory";
import { useGetBranchResourcesQuery } from "../../services/biometric";

/* ─── Types ──────────────────────────────────────────────────────────── */

interface InventorySelection {
  selected: boolean;
  quantity: number;
}

interface InventorySelectionProps {
  value?:    Record<string, InventorySelection>;
  onChange?: (val: Record<string, InventorySelection>) => void;
}

interface InventoryOption {
  label:    string;
  value:    string;
  disabled: boolean;
}

interface BiometricOption {
  label:    string;
  value:    string;
  disabled: boolean;
}

/* ─── Constants ──────────────────────────────────────────────────────── */

const packageTypeOptions = [
  { label: 'Membership',          value: 'membership'          },
  { label: 'Trial',               value: 'trial'               },
  { label: 'Membership Transfer', value: 'membership_transfer' },
];

const gymKitOptions = [
  { label: 'Yes', value: 'yes' },
  { label: 'No',  value: 'no'  },
];

const assessmentOptions = [
  { label: 'Yes', value: 'yes' },
  { label: 'No',  value: 'no'  },
];

const accessTypeOptions = [
  { label: 'Peak Hours',     value: 'peak_hours'     },
  { label: 'Non-Peak Hours', value: 'non_peak_hours' },
];

const breadcrumbItems = [
  { label: <HomeOutlined />, to: Home },
  { label: "All Packages", to: AllPackagesRoute },
  { label: "Add Package" },
];

/* ─── Component ──────────────────────────────────────────────────────── */

const AddPackage = () => {
  const nav  = useNavigate();
  const [form] = Form.useForm();

  const { data: branchesData }                    = useGetBranchesQuery(undefined);
  const [triggerAddPackage, { isLoading }]         = useAddPlanMutation();
  const [gymKitSelected,   setGymKitSelected]      = useState('no');
  const [couponSelected,   setCouponSelected]      = useState('no');
  const [accessType,       setAccessType]          = useState('peak_hours');

  const selectedBranches: string[] | undefined = Form.useWatch('branch', form);

  const { data: inventoryData } = useGetGymKitInventoryQuery(
    selectedBranches && selectedBranches.length > 0 ? selectedBranches : undefined,
    { skip: gymKitSelected !== 'yes' || !selectedBranches || selectedBranches.length === 0 },
  );

  const { data: biometricData } = useGetBranchResourcesQuery(
    selectedBranches && selectedBranches.length > 0 ? selectedBranches : undefined,
    { skip: !selectedBranches || selectedBranches.length === 0 },
  );

  useEffect(() => {
    if (!selectedBranches || selectedBranches.length === 0) {
      form.setFieldsValue({ selectedInventory: undefined, biometricMachine: undefined });
    }
  }, [selectedBranches, form]);

  const branchOptions = useMemo(() => {
    const data = (branchesData as any)?.data;
    if (!Array.isArray(data)) return [];
    return data.map((b: any) => ({
      label: `${b.name}${b.branchArea ? ` — ${b.branchArea}` : ''}`,
      value: b._id,
    }));
  }, [branchesData]);

  const inventoryOptions = useMemo<InventoryOption[]>(() => {
    if (!selectedBranches?.length) return [];
    const data = (inventoryData as any);
    if (!data?.success || !Array.isArray(data.data)) return [];
    const all: InventoryOption[] = [];
    data.data.forEach((branchData: any) => {
      if (Array.isArray(branchData.inventory)) {
        branchData.inventory.forEach((item: any) => {
          all.push({
            label:    `${item.productName} - ${branchData.branchInfo.name} (Qty: ${item.quantity}) - ${item.warehouseName}`,
            value:    item._id,
            disabled: item.quantity === 0,
          });
        });
      }
    });
    return all;
  }, [inventoryData, selectedBranches]);

  const biometricMachineOptions = useMemo<BiometricOption[]>(() => {
    if (!selectedBranches?.length) return [];
    const data = (biometricData as any);
    if (!data?.success || !Array.isArray(data.data?.machines)) return [];
    const all: BiometricOption[] = [];
    data.data.machines.forEach((branchData: any) => {
      if (Array.isArray(branchData.machines)) {
        branchData.machines.forEach((machine: any) => {
          all.push({
            label:    `${machine.machineId} - ${branchData.branchInfo.name} (${machine.floor || 'N/A'}) - ${machine.recordPurpose?.toUpperCase() || 'N/A'}`,
            value:    machine._id,
            disabled: machine.deleted_at !== null,
          });
        });
      }
    });
    return all;
  }, [biometricData, selectedBranches]);

  /* ── Inventory selection sub-component ── */
  const InventorySelectionWithQuantity = ({ value, onChange }: InventorySelectionProps) => {
    const [localSelection, setLocalSelection] = useState<Record<string, InventorySelection>>(value || {});

    const handleItemCheck = (itemId: string, checked: boolean) => {
      const next = { ...localSelection };
      if (checked) next[itemId] = { selected: true, quantity: 1 };
      else delete next[itemId];
      setLocalSelection(next);
      onChange?.(next);
    };

    const handleQuantityChange = (itemId: string, qty: number | null) => {
      if (!localSelection[itemId]) return;
      const validQty = Math.max(qty ?? 1, 1);
      const next = { ...localSelection, [itemId]: { ...localSelection[itemId], quantity: validQty } };
      setLocalSelection(next);
      onChange?.(next);
    };

    return (
      <div style={{ maxHeight: 300, overflowY: 'auto', padding: 12, border: '1px solid #d9d9d9', borderRadius: 6 }}>
        {inventoryOptions.map(option => {
          const isSelected = localSelection[option.value]?.selected || false;
          const quantity   = localSelection[option.value]?.quantity  || 1;
          return (
            <Row key={option.value} align="middle" style={{ marginBottom: 12 }}>
              <Col span={16}>
                <Checkbox checked={isSelected} disabled={option.disabled} onChange={e => handleItemCheck(option.value, e.target.checked)}>
                  {option.label}
                </Checkbox>
              </Col>
              {isSelected && (
                <Col span={8}>
                  <Space>
                    <span style={{ fontSize: 14, color: '#666' }}>Qty:</span>
                    <InputNumber min={1} value={quantity} onChange={val => handleQuantityChange(option.value, val)} size="small" style={{ width: 80, height: 30 }} />
                  </Space>
                </Col>
              )}
            </Row>
          );
        })}
        {inventoryOptions.length === 0 && (
          <div style={{ textAlign: 'center', color: '#999', padding: 20 }}>
            No inventory items available for selected branch
          </div>
        )}
      </div>
    );
  };

  /* ── Helpers ── */
  const onFinishFailed = ({ errorFields }: { errorFields: { name: (string | number)[]; errors: string[] }[] }) => {
    if (!errorFields?.length) return;
    const labels = errorFields.map(f => {
      const name = Array.isArray(f.name) ? f.name[0] : f.name;
      return String(name).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    });
    notification.error({ message: 'Required Fields Missing', description: [...new Set(labels)].join(', '), placement: 'topRight', duration: 4 });
  };

  const buildProducts = (selectedInventory: Record<string, InventorySelection> | undefined) =>
    selectedInventory
      ? Object.entries(selectedInventory).map(([productId, d]) => ({ productId, quantity: d.quantity }))
      : [];

  const handleAddPackage = async (values: Record<string, any>) => {
    if (values.packageType === 'membership_transfer') {
      const payload = {
        type:              values.packageType,
        name:              values.packageName,
        pricing:           values.price,
        hsnSac:            values.hsnCode,
        branchIds:         Array.isArray(values.branch) ? values.branch : [values.branch],
        gymKitAvailable:   values.gymKit === 'yes',
        products:          values.gymKit === 'yes' ? buildProducts(values.selectedInventory) : [],
        biometricMachines: values.selectedBiometric || [],
        machineIds:        values.selectedBiometric || [],
      };
      try {
        await (triggerAddPackage as any)(payload).unwrap();
        nav(AllPackagesRoute);
      } catch (error) {
        console.error('Failed to add package', error);
      }
      return;
    }

    const base = {
      name:                  values.packageName,
      pricing:               values.price,
      numberOfDays:          values.validDays,
      hsnSac:                values.hsnCode,
      freezable:             values.freezable === 'yes',
      ...(values.freezable === 'yes' && { freezableDays: values.freezeDays, freezableSlot: values.freezeSlot }),
      accessType:            values.accessType || 'peak_hours',
      ...(values.accessType === 'non_peak_hours' && { nonPeakHours: values.nonPeakHours || [] }),
      branchIds:             Array.isArray(values.branch) ? values.branch : [values.branch],
      benefits:              (values.benefitPoints || []).map((bp: any) => ({ type: bp.point })),
      complementaryServices: (values.complementaryServices || []).map((cs: any) => ({
        serviceType:      cs.serviceType.toLowerCase().replace(/\s+/g, '_'),
        numberOfSessions: cs.sessions,
      })),
      photos:                values.banner ? [values.banner] : [],
      description:           values.description || '',
      preferredGender:       values.preferredGender || 'any',
      maxMembers:            values.maxMembers || 0,
      numberOfAssessment:    values.assessment === 'yes' ? values.numberOfAssessments : 0,
      hasAssessments:        values.assessment === 'yes',
      gymKitAvailable:       values.gymKit === 'yes',
      products:              values.gymKit === 'yes' ? buildProducts(values.selectedInventory) : [],
      biometricMachines:     values.selectedBiometric || [],
      machineIds:            values.selectedBiometric || [],
    };

    let payload: Record<string, any>;

    if (values.packageType === 'add_on_package') {
      payload = {
        ...base,
        type:              values.packageType,
        addonPackageName:  values.addonPackageName,
        addonPackagePrice: values.addonPackagePrice,
        numberOfSession:   values.numberOfSession,
        upgradeLimit:      values.upgradeLimit || 0,
        renewLimit:        values.advanceRenewDays || 0,
      };
    } else {
      payload = {
        ...base,
        type:          values.packageType,
        upgradeLimit:  values.packageType === 'membership' ? values.upgradeLimit  : 0,
        renewLimit:    values.packageType === 'membership' ? values.advanceRenewDays : 0,
        numberOfCoupons: values.numberOfCoupons,
        ...(values.couponApplicable === 'yes' && { numberOfCoupons: values.numberOfCoupons }),
      };
    }

    try {
      await (triggerAddPackage as any)(payload).unwrap();
      nav(AllPackagesRoute);
    } catch (error) {
      console.error('Failed to add package', error);
    }
  };

  /* ── Shared JSX helpers ── */
  const BiometricCheckboxGroup = () => (
    <div className="form-row-single">
      <Form.Item label="Select Biometric Machines" name="selectedBiometric">
        <Checkbox.Group>
          <Row>
            {biometricMachineOptions.map(option => (
              <Col span={24} key={option.value} style={{ marginBottom: 8 }}>
                <Checkbox value={option.value} disabled={option.disabled}>{option.label}</Checkbox>
              </Col>
            ))}
            {biometricMachineOptions.length === 0 && (
              <Col span={24}>
                <div style={{ textAlign: 'center', color: '#999', padding: 20 }}>
                  No biometric machines available for selected branches
                </div>
              </Col>
            )}
          </Row>
        </Checkbox.Group>
      </Form.Item>
    </div>
  );

  const GymKitBlock = () => (
    <>
      <div className="form-row-single">
        <Form.Item label="Gym Kit" name="gymKit" rules={[{ required: true, message: 'Please select gym kit option' }]}>
          <Select
            placeholder="Select gym kit option"
            options={gymKitOptions}
            onChange={(value: string) => {
              setGymKitSelected(value);
              if (value === 'no') form.setFieldsValue({ selectedInventory: [] });
            }}
          />
        </Form.Item>
      </div>
      {gymKitSelected === 'yes' && (
        <div className="form-row-single">
          <Form.Item label="Select Inventory Items with Quantities" name="selectedInventory" rules={[{ required: true, message: 'Please select inventory items' }]}>
            <InventorySelectionWithQuantity />
          </Form.Item>
        </div>
      )}
    </>
  );

  const NonPeakHoursList = () => (
    <Form.Item noStyle shouldUpdate={(p, c) => p.accessType !== c.accessType}>
      {({ getFieldValue }) =>
        getFieldValue('accessType') === 'non_peak_hours' ? (
          <>
            <div style={{ marginBottom: 16 }}><h4 style={{ marginBottom: 12 }}>Non-Peak Hours</h4></div>
            <Form.List name="nonPeakHours" initialValue={[{ from: '', to: '' }]}>
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...rest }) => (
                    <div key={key} className="form-row" style={{ marginBottom: 12 }}>
                      <Form.Item {...rest} label="From Time" name={[name, 'from']} rules={[{ required: true, message: 'Please select start time' }]}>
                        <TimePicker format="HH:mm" placeholder="10:00" style={{ width: '100%' }} />
                      </Form.Item>
                      <Form.Item {...rest} label="To Time" name={[name, 'to']} rules={[{ required: true, message: 'Please select end time' }]}>
                        <TimePicker format="HH:mm" placeholder="17:00" style={{ width: '100%' }} />
                      </Form.Item>
                      {fields.length > 1 && (
                        <MinusCircleOutlined onClick={() => remove(name)} style={{ fontSize: 20, color: 'red', marginTop: 30, cursor: 'pointer' }} />
                      )}
                    </div>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} style={{ width: '100%' }}>Add Time Slot</Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </>
        ) : null
      }
    </Form.Item>
  );

  const BenefitPointsList = () => (
    <div className="certificates-section">
      <h3 style={{ color: 'var(--sider-text)', marginBottom: 16, marginTop: 0 }}>Benefit Points</h3>
      <Form.List name="benefitPoints">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...rest }) => (
              <div key={key} className="certificate-item">
                <div className="row">
                  <Form.Item {...rest} name={[name, 'point']} label="Benefit Point" rules={[{ required: true, message: 'Please enter benefit point' }]}>
                    <Input placeholder="Enter benefit point" />
                  </Form.Item>
                  <Button type="link" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} style={{ marginTop: 30 }}>Remove</Button>
                </div>
              </div>
            ))}
            <Form.Item>
              <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>Add Benefit Point</Button>
            </Form.Item>
          </>
        )}
      </Form.List>
    </div>
  );

  return (
    <div className="add-package-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>Add Package</h2>
        <PageBreadcrumb items={breadcrumbItems} />
      </div>

      <div className="form-container">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddPackage}
          onFinishFailed={onFinishFailed}
          className="trainer-form"
          initialValues={{ packageType: "membership", freezable: "no", accessType: "peak_hours" }}
        >
          {/* Row 1: Package Type + Package Name */}
          <div className="form-row">
            <Form.Item label="Package Type" name="packageType" rules={[{ required: true, message: 'Please select package type' }]}>
              <Select placeholder="Select package type" options={packageTypeOptions} />
            </Form.Item>

            <Form.Item noStyle shouldUpdate={(p, c) => p.packageType !== c.packageType}>
              {({ getFieldValue }) => {
                const pt = getFieldValue('packageType');
                if (!pt) return null;
                return (
                  <Form.Item label="Package Name" name="packageName" rules={[{ required: true, message: 'Please enter package name' }]}>
                    <Input placeholder="Enter package name" />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </div>

          {/* Conditional body based on packageType */}
          <Form.Item noStyle shouldUpdate={(p, c) => p.packageType !== c.packageType}>
            {({ getFieldValue }) => {
              const packageType: string = getFieldValue('packageType');

              /* ── Membership Transfer ── */
              if (packageType === 'membership_transfer') {
                return (
                  <>
                    <div className="form-row">
                      <Form.Item label="Price" name="price" rules={[{ required: true, message: 'Please enter price' }]}>
                        <InputNumber placeholder="Enter price" min={0} style={{ width: '100%' }} prefix="₹" />
                      </Form.Item>
                      <Form.Item label="HSN/SAC Code" name="hsnCode" rules={[{ required: true, message: 'Please enter HSN/SAC code' }]}>
                        <Input placeholder="Enter HSN/SAC code" />
                      </Form.Item>
                    </div>
                    <div className="form-row-single">
                      <Form.Item label="Branch" name="branch" rules={[{ required: true, message: 'Please select at least one branch' }]}>
                        <Select mode="multiple" placeholder="Select branches" options={branchOptions} onChange={() => form.setFieldsValue({ selectedInventory: [] })} />
                      </Form.Item>
                    </div>
                    <GymKitBlock />
                    <BiometricCheckboxGroup />
                  </>
                );
              }

              /* ── Membership / Trial ── */
              if (packageType === 'membership' || packageType === 'trial') {
                return (
                  <>
                    <div className="form-row">
                      <Form.Item label="Price" name="price" rules={[{ required: true, message: 'Please enter price' }]}>
                        <InputNumber placeholder="Enter price" min={0} style={{ width: '100%' }} prefix="₹" />
                      </Form.Item>
                      <Form.Item label="Assessment" name="assessment" rules={[{ required: true, message: 'Please select assessment' }]}>
                        <Select placeholder="Select assessment" options={assessmentOptions} />
                      </Form.Item>
                    </div>

                    <Form.Item noStyle shouldUpdate={(p, c) => p.assessment !== c.assessment}>
                      {({ getFieldValue: gfv }) =>
                        gfv('assessment') === 'yes' ? (
                          <div className="form-row-single">
                            <Form.Item label="Number of Assessments" name="numberOfAssessments" rules={[{ required: true, message: 'Please enter number of assessments' }]}>
                              <InputNumber placeholder="Enter number of assessments" min={1} style={{ width: '100%' }} />
                            </Form.Item>
                          </div>
                        ) : null
                      }
                    </Form.Item>

                    {packageType === 'membership' && (
                      <>
                        <div className="form-row">
                          <Form.Item label="Upgrade Limit" name="upgradeLimit" rules={[{ required: true, message: 'Please enter upgrade limit' }]}>
                            <InputNumber placeholder="Enter upgrade limit" min={0} style={{ width: '100%' }} />
                          </Form.Item>
                          <Form.Item label="Advance Renew Days" name="advanceRenewDays" rules={[{ required: true, message: 'Please enter advance renew days' }]}>
                            <InputNumber placeholder="Enter advance renew days" min={0} style={{ width: '100%' }} />
                          </Form.Item>
                        </div>
                        <div className="form-row-single">
                          <Form.Item label="Freezable" name="freezable" rules={[{ required: true, message: 'Please select freezable option' }]} initialValue="no">
                            <Select placeholder="Select freezable option" options={[{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }]} />
                          </Form.Item>
                        </div>
                        <Form.Item noStyle shouldUpdate={(p, c) => p.freezable !== c.freezable}>
                          {({ getFieldValue: gfv }) =>
                            gfv('freezable') === 'yes' ? (
                              <div className="form-row">
                                <Form.Item label="Freeze Slot" name="freezeSlot" rules={[{ required: true, message: 'Please enter freeze slot' }]}>
                                  <InputNumber placeholder="Enter freeze slot" min={1} style={{ width: '100%' }} />
                                </Form.Item>
                                <Form.Item label="Freeze Days" name="freezeDays" rules={[{ required: true, message: 'Please enter freeze days' }]}>
                                  <InputNumber placeholder="Enter freeze days" min={1} style={{ width: '100%' }} />
                                </Form.Item>
                              </div>
                            ) : null
                          }
                        </Form.Item>
                      </>
                    )}

                    <div className="form-row-single">
                      <Form.Item label="Number of Valid Days" name="validDays" rules={[{ required: true, message: 'Please enter number of valid days' }]}>
                        <InputNumber placeholder="Enter number of valid days" min={1} style={{ width: '100%' }} />
                      </Form.Item>
                    </div>

                    <div className="form-row-single">
                      <Form.Item label="Access Type" name="accessType" rules={[{ required: true, message: 'Please select access type' }]}>
                        <Select placeholder="Select access type" options={accessTypeOptions} onChange={(v: string) => setAccessType(v)} />
                      </Form.Item>
                    </div>

                    <NonPeakHoursList />

                    <div className="form-row">
                      <Form.Item label="HSN Code" name="hsnCode" rules={[{ required: true, message: 'Please enter HSN code' }]}>
                        <Input placeholder="Enter HSN code" />
                      </Form.Item>
                      <Form.Item label="Branch" name="branch" rules={[{ required: true, message: 'Please select at least one branch' }]}>
                        <Select mode="multiple" placeholder="Select branches" options={branchOptions} onChange={() => form.setFieldsValue({ selectedInventory: [] })} />
                      </Form.Item>
                    </div>

                    <GymKitBlock />

                    {packageType === 'membership' && (
                      <>
                        <div className="form-row-single">
                          <Form.Item label="Coupon Applicable" name="couponApplicable" rules={[{ required: true, message: 'Please select coupon option' }]}>
                            <Select
                              placeholder="Select Coupon option"
                              options={gymKitOptions}
                              onChange={(value: string) => {
                                setCouponSelected(value);
                                if (value === 'no') form.setFieldsValue({ numberOfCoupons: undefined });
                              }}
                            />
                          </Form.Item>
                        </div>
                        {couponSelected === 'yes' && (
                          <div className="form-row-single">
                            <Form.Item label="Number of Coupons" name="numberOfCoupons" rules={[{ required: true, message: 'Please enter number of coupons' }]}>
                              <InputNumber min={1} style={{ width: '100%' }} placeholder="Enter number of coupons" />
                            </Form.Item>
                          </div>
                        )}
                      </>
                    )}

                    <BiometricCheckboxGroup />

                    <div className="form-row-single">
                      <Form.Item
                        label="Banner"
                        name="banner"
                        rules={[{ validator: (_: unknown, value: string) => value ? Promise.resolve() : Promise.reject('Please upload banner image') }]}
                      >
                        <ImagePicker form={form} name="banner" />
                      </Form.Item>
                    </div>

                    <BenefitPointsList />
                  </>
                );
              }

              return null;
            }}
          </Form.Item>

          <div className="form-actions">
            <Button type="primary" htmlType="submit" loading={isLoading} className="submit-btn">
              Add Package
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default AddPackage;
